import {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { DataTable } from "~/components/data-table";
import { Flex } from "~/components/Flex";
import { Check as CheckIcon } from "@mui/icons-material";
import { Loading } from "~/components/loading";
import { SpaceScope } from "~/hooks/useScope";
import { ExceptionReviewAction } from "~/operations";
import { usePendingReview } from "../hooks/usePendingReview";
import { useSpaceExceptions } from "../hooks/useSpaceExceptions";
import { PendingExceptionsModal } from "./PendingExceptionsModal";
import { EmptyPendingExceptionsCard } from "./EmptyCards";
import { HubCard } from "~/components/hub-card";
import { ExceptionGroup } from "../types";
import { ListCountChip } from "./ListCountChip";

export type PendingExceptionsCardProps = {
  spaceScope: SpaceScope;
  onReviewActionChange?: (
    value: ExceptionGroup,
    action: ExceptionReviewAction,
  ) => void;
  isReviewLoading: boolean;
  loadingAction: ExceptionReviewAction | undefined;
  loadingExceptionId: string | undefined;
};

export function PendingExceptionsCard({
  spaceScope,
  onReviewActionChange,
  isReviewLoading = false,
  loadingAction,
  loadingExceptionId,
}: PendingExceptionsCardProps) {
  const { pendingExceptions } = useSpaceExceptions({
    scopeMrn: spaceScope.mrn,
  });
  const pendingExceptionGroupEdges =
    pendingExceptions.data?.listExceptionGroups.edges || [];

  const pr = usePendingReview({ scopeMrn: spaceScope.mrn });
  const totalCount =
    pendingExceptions.data?.listExceptionGroups.totalCount || 0;

  const isEmpty = !pendingExceptions.loading && totalCount === 0;

  const reviewItemUrl = (afterCursor?: string) => {
    return pr.paginate.cursorUrl(pr.openUrl.href, { afterCursor });
  };

  return (
    <HubCard
      loading={pendingExceptions.loading}
      title={
        <Flex alignItems="center" gap={1}>
          Needs Review
          {totalCount > 0 && <ListCountChip count={totalCount} />}
        </Flex>
      }
      icon={<CheckIcon fontSize="small" />}
      sx={{ height: "100%", justifyContent: "space-between" }}
      data-testid="pending-exceptions-card"
    >
      {isEmpty && <EmptyPendingExceptionsCard />}
      {!isEmpty && (
        <Flex
          flexDirection={"column"}
          justifyContent={"space-between"}
          gap={2}
          minHeight={"100%"}
        >
          <DataTable firewatchSize={"small"}>
            <TableHead
              sx={{
                backgroundColor: "inherit",
                boxShadow: "none",
                "& th": {
                  fontSize: 10,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  borderBottom: "none",
                },
              }}
            >
              <TableRow>
                <TableCell>Exception ID</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Review Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* If the data is loading, show a loading message */}
              {pendingExceptions.loading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box sx={{ my: 10 }}>
                      <Loading what="Pending Exceptions" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pendingExceptionGroupEdges.map((edge, index) => {
                  const eg = edge.node;
                  // const prevEdge = index > 0 ? pendingExceptionGroupEdges.at(index - 1) : undefined;
                  // TODO: `afterCursor` will need to be `prevEdge.cursor` when listExceptionsGroups endCursor gets fixed
                  const afterCursor = index > 0 ? edge.cursor : undefined;
                  return (
                    <TableRow
                      key={eg.exceptionId}
                      sx={{
                        "& td": { fontSize: 12 },
                      }}
                      component={RouterLink}
                      to={reviewItemUrl(afterCursor).href}
                    >
                      <TableCell>{eg.exceptionId}</TableCell>
                      <TableCell>{eg.author?.name}</TableCell>
                      <TableCell>{eg.exceptions.length} Checks</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {eg.status.toLowerCase().replace(/_/g, " ")}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </DataTable>
          <Flex justifyContent={"center"}>
            <Button
              color="primary"
              component={RouterLink}
              to={pr.openUrl.href}
              sx={{ fontSize: 12, fontWeight: 500 }}
            >
              View All
            </Button>
          </Flex>
        </Flex>
      )}
      {totalCount > 0 && (
        <PendingExceptionsModal
          spaceScope={spaceScope}
          onReviewActionChange={onReviewActionChange}
          isReviewLoading={isReviewLoading}
          loadingAction={loadingAction}
          loadingExceptionId={loadingExceptionId}
          totalCount={totalCount}
        />
      )}
    </HubCard>
  );
}
